/**
 * The console's Better Auth client.
 *
 * Same origin and the same path the console Worker forwards unmodified, so the session
 * cookie is first-party and nothing about the session is stored in JavaScript where a
 * cross-site script could read it. This mirrors the server config in
 * `apps/console/api/src/auth/settings.ts`: the plugins here are the ones `createAuth` registers,
 * and a plugin present on one side and not the other is a method that exists and cannot
 * be called.
 *
 * `organizationClient` is what makes a person's active organisation part of the session
 * the console reads, and `multiSessionClient` is what lets them hold more than one at
 * once — the two things that make `sessions.tenant_id` unnecessary rather than merely
 * inconvenient. `emailOTPClient` is the passwordless front door: it adds
 * `emailOtp.sendVerificationOtp` and `signIn.emailOtp`. `passkeyClient` adds the
 * WebAuthn sign-in and registration calls.
 *
 * ## The base URL is absolute on purpose
 *
 * Better Auth validates its client `baseURL` with `new URL(...)`, and a bare path like
 * `/auth` is not a URL — it throws `Invalid base URL` while the module is being
 * evaluated, before React ever mounts. That is not an error the console can render: it
 * is a blank page, because the import that would have drawn something never finished.
 * Composing the origin here keeps the path the server routes on (`/auth`) while giving
 * the library the absolute URL it insists on.
 */
import { createAuthClient } from 'better-auth/react'
import { emailOTPClient, organizationClient, multiSessionClient } from 'better-auth/client/plugins'
import { passkeyClient } from '@better-auth/passkey/client'
import type { BetterAuthClientPlugin } from 'better-auth/client'
import { tabSyncClient } from '@layerit/auth/tab-sync'

/**
 * The same-origin origin, with a server-side fallback.
 *
 * The console is a browser application, but a module can still be imported while Vite
 * resolves a test or prerenders; the fallback keeps that from being a crash rather than
 * only a base URL that cannot be reached.
 */
function authOrigin(): string {
	return typeof window === 'undefined' ? 'http://localhost' : window.location.origin
}

/**
 * Signing out another of your sessions, by id: the server's `session-revocation` plugin.
 *
 * The session table stores a digest of each token, so the token `listSessions` reports for
 * another session is that digest, and Better Auth's own `revokeSession({ token })` would
 * match nothing while answering success. These two calls go by session id instead.
 */
export function sessionRevocationClient() {
	return {
		id: 'session-revocation',
		getActions: ($fetch) => ({
			sessionRevocation: {
				revoke: async (id: string) => $fetch<{ status: boolean }>('/session/revoke', { method: 'POST', body: { id } }),
				revokeOthers: async () => $fetch<{ status: boolean }>('/session/revoke-others', { method: 'POST', body: {} }),
			},
		}),
	} satisfies BetterAuthClientPlugin
}

export const authClient = createAuthClient({
	baseURL: `${authOrigin()}/auth`,
	plugins: [
		emailOTPClient(),
		organizationClient(),
		multiSessionClient(),
		passkeyClient(),
		sessionRevocationClient(),
		tabSyncClient('layerit.console.session'),
	],
})

export type AuthClient = typeof authClient
